import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router';
import { useDialogs } from '../../hooks/useDialogs/useDialogs';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import PageContainer from '../layout/PageContainer';
import { GET_INVENTORYITEMS_QUERY, GET_INVENTORYITEM_BY_ID_QUERY } from '../../queries/InventoryItemQueries';
import type { InventoryItem } from '../../models/InventoryItem';
import { DELETE_INVENTORYITEM_BY_ID_MUTATION } from '../../mutations/InventoryItemMutations';
import dayjs from "dayjs";
import { UnitType, unitTypeToUnit, unitTypeToUnits } from "../../models/Item";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { ConsumedInventoryItem } from "../../models/ConsumedInventoryItem";
import { toUSCurrency } from '../../utilities/CurrencyUtilities';
import type { Document } from '../../models/Document';

export default function InventoryItemShow() {
  const { inventoryItemId } = useParams();
  const navigate = useNavigate();

  const dialogs = useDialogs();
  const notifications = useNotifications();

  const [getInventoryItemById] = useLazyQuery(
    GET_INVENTORYITEM_BY_ID_QUERY);

  const [deleteInventoryItemById] = useMutation(
    DELETE_INVENTORYITEM_BY_ID_MUTATION, {
    refetchQueries: [{ query: GET_INVENTORYITEMS_QUERY }],
  });

  const [inventoryItem, setInventoryItem] = useState<InventoryItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadData = useCallback(async () => {
    setError(null);

    const id = +(inventoryItemId || '0');

    if (id > 0) {
      setIsLoading(true);

      try {
        const { data, error: getInventoryItemByIdError } = await getInventoryItemById({ variables: { id } });

        setIsLoading(false);
        setError(null);

        if (getInventoryItemByIdError) {
          setError(getInventoryItemByIdError);
        }

        if (data?.inventoryItem) {
          setInventoryItem(data.inventoryItem);
        } else {
          setError({ message: 'No inventory item found' } as Error);
        }
      } catch (showDataError) {
        setError(showDataError as Error);
      }
    }
    setIsLoading(false);
  }, [getInventoryItemById, inventoryItemId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEdit = useCallback(() => {
    navigate(`/dashboard/inventory-items/${inventoryItemId}/edit`);
  }, [inventoryItemId, navigate]);

  const handleDelete = useCallback(async () => {
    if (!inventoryItem) {
      return;
    }

    const confirmed = await dialogs.confirm(
      `Do you wish to delete ${inventoryItem.item?.name}?`,
      {
        title: `Delete inventory item?`,
        severity: 'error',
        okText: 'Delete',
        cancelText: 'Cancel',
      },
    );

    if (confirmed) {
      setIsLoading(true);
      try {
        const { data: result } = await deleteInventoryItemById({ variables: { id: inventoryItem.id } });

        if (result?.deleted) {
          navigate('/dashboard/items');

          notifications.show('Inventory item deleted successfully.', {
            severity: 'success',
            autoHideDuration: 3000,
          });
        } else {
          notifications.show(
            'Failed to delete inventory item. Reason: Unknown.',
            {
              severity: 'error',
              autoHideDuration: 3000,
            },
          );
        }
      } catch (deleteError) {
        notifications.show(
          `Failed to delete inventory item. Reason:' ${(deleteError as Error).message}`,
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
      }
      setIsLoading(false);
    }
  }, [inventoryItem, dialogs, deleteInventoryItemById, navigate, notifications]);

  const handleBack = useCallback(() => {
    navigate('/dashboard/inventory-items');
  }, [navigate]);

  const renderShow = useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    return inventoryItem ? (
      <Box sx={{ flexGrow: 1, width: '100%' }}>
        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid size={{ xs: 9, sm: 3 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Name</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <a href={`/dashboard/items/${inventoryItem.itemId}`}>{inventoryItem.item?.name} ({inventoryItem.item?.isIngredient ? 'Ingredient' : 'None-Ingredient'})</a>
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 7, sm: 3 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Vendor</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <a href={`/dashboard/vendors/${inventoryItem.vendorId}`}>{inventoryItem.vendor?.name}</a>
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 9, sm: 3 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Lot Number</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {inventoryItem.lotNumber || 'N/A'}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 9, sm: 3 }}>
            <Paper sx={{ px: 2, py: 1, color: (inventoryItem.quantityOnHand ?? 0) < (inventoryItem.item?.minOrderQuantity || 0) ? '#fac1c0' : '' }}>
              <Typography variant="overline">Quantity on Hand</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {(inventoryItem.quantityOnHand ?? 0).toFixed(3)} {unitTypeToUnits(inventoryItem.item?.unitType as UnitType)}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 10, sm: 4 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Date Ordered</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <a href={`/dashboard/purchase-orders/${inventoryItem.purchaseOrderId}`}>{dayjs(inventoryItem.purchaseOrder?.dateTimePurchased).format('M/D/YYYY')}</a>
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 10, sm: 4 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Ordered Quantity</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {inventoryItem.quantityOrdered.toFixed(3)} {unitTypeToUnits(inventoryItem.item?.unitType as UnitType)}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 10, sm: 4 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Order Details</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {inventoryItem.orderDetails || 'N/A'}
              </Typography>
            </Paper>
          </Grid>
          <Grid container spacing={2} sx={{ width: '100%' }}>
            <Grid size={{ xs: 6, sm: 2 }}>
              <Paper sx={{ px: 2, py: 1 }}>
                <Typography variant="overline">Cost per Unit</Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  ${inventoryItem.costPerUnit.toFixed(2)} /{unitTypeToUnit(inventoryItem.item?.unitType as UnitType)}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <Paper sx={{ px: 2, py: 1 }}>
                <Typography variant="overline">Surcharge per Unit</Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  ${(inventoryItem.surchargePerUnit || 0).toFixed(2)} /{unitTypeToUnit(inventoryItem.item?.unitType as UnitType)}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <Paper sx={{ px: 2, py: 1 }}>
                <Typography variant="overline">Shipping Cost per Unit</Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  ${(inventoryItem.shippingCostPerUnit || 0).toFixed(2)} /{unitTypeToUnit(inventoryItem.item?.unitType as UnitType)}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <Paper sx={{ px: 2, py: 1 }}>
                <Typography variant="overline">Surcharge/Discount</Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  ${(inventoryItem.surchargeOrDiscount || 0).toFixed(2)}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <Paper sx={{ px: 2, py: 1 }}>
                <Typography variant="overline">Total Cost per Unit</Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  ${toUSCurrency(inventoryItem.totalCostPerUnit)} /{unitTypeToUnit(inventoryItem.item?.unitType as UnitType)}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <Paper sx={{ px: 2, py: 1 }}>
                <Typography variant="overline">Total Ordered Cost</Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  ${toUSCurrency(inventoryItem.totalOrderedCost)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ width: '100%' }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Paper sx={{ px: 2, py: 1 }}>
                <Typography variant="overline">Attachments</Typography>
                {(inventoryItem.attachments || []).length > 0 ?
                  <TableContainer sx={{ maxHeight: '150px' }}>
                    <Table stickyHeader sx={{ minWidth: 500 }} aria-label="dense table" size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '100%' }} component="th" scope="row" align="left">Name</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(inventoryItem.attachments || []).map((attachment: Document) => (
                          <TableRow
                            key={attachment.id}
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                          >
                            <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '100%' }} align="left">
                              <a href={`${process.env.REACT_APP_API_URL}${attachment.relativePath}`} target="_blank" rel="noopener noreferrer">
                                {attachment.name}
                              </a>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  : <Box>None</Box>}
              </Paper>
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ width: '100%' }}>
            <Grid size={{ xs: 12, sm: 12 }}>
              <Paper sx={{ px: 2, py: 1 }}>
                <Typography variant="overline">Batches</Typography>
                {(inventoryItem.consumedInventoryItems || []).length > 0 ?
                  <TableContainer sx={{ maxHeight: '150px' }}>
                    <Table stickyHeader sx={{ minWidth: 500 }} aria-label="dense table" size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '40%' }} component="th" scope="row" align="left">Batch Lot #</TableCell>
                          <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '40%' }} component="th" scope="row" align="right">Date Created</TableCell>
                          <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }} component="th" scope="row" align="right">Qty Used (lbs)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(inventoryItem.consumedInventoryItems || []).map((consumedInventoryItem: ConsumedInventoryItem) => (
                          <TableRow
                            key={consumedInventoryItem.id}
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                          >
                            <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} align="left">
                              <a href={`/dashboard/batches/${consumedInventoryItem.batchId}`}>{consumedInventoryItem.batch?.lotNumber}</a>
                            </TableCell>
                            <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '10%' }} align="right">
                              {dayjs(consumedInventoryItem.batch?.dateTimeCreated).format('M/D/YYYY')}
                            </TableCell>
                            <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '10%' }} align="right">
                              {(consumedInventoryItem.quantityUsed || 0).toFixed(3)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer> : <Box>None</Box>
                }
              </Paper>
            </Grid>
          </Grid>
        </Grid>
        <Divider sx={{ my: 3 }} />
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back
          </Button>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEdit}
            >
              Edit
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </Stack>
        </Stack>
      </Box>
    ) : null;
  }, [isLoading, error, inventoryItem, handleBack, handleEdit, handleDelete]);

  const pageTitle = `${inventoryItem?.item?.name} (${inventoryItem?.vendor?.name}: ${inventoryItem?.lotNumber || 'N/A'})`;

  return (
    <PageContainer
      title={pageTitle}
      breadcrumbs={[
        { title: 'Items', path: '/dashboard/items' },
        { title: pageTitle },
      ]}
    >
      <Box sx={{ display: 'flex', flex: 1, width: '100%' }}>{renderShow}</Box>
    </PageContainer>
  );
}
